/**
 * THE ONLY AAOB VALUE TYPE FORGE GENERATES (2026-08-10). One constant, so widening is one edit.
 *
 * An AAOB row is `(systemKey, systemType, value)` — a runtime column's NAME, TYPE and value — and the
 * upsert matches on the PAIR `(systemKey, systemType)`. The type is therefore part of the column's
 * IDENTITY, not a formatting choice: writing "quantityRequired" as Number when the column is String does
 * not convert anything, it creates a SECOND runtime column beside the first, silently.
 *
 * String is the only type the blocks offer (`set_key_value_in_AOB_reference`'s TYPE dropdown is
 * String-only; `aaob_aggregate_by_label` has no type field at all), so every other value was already
 * unreachable — but the step schema still carried `valueType` / `aaobType`, the prompt still described
 * them, and both compilers honoured whatever arrived. That is a slot where the only possible non-default
 * answer is a silent duplicate column. It is now decided by code.
 *
 * SCOPE — generation only. `blockly-to-steps.ts` still reads whatever a saved block stores, because a
 * legacy save (4 `aaob_sort_by_label` blocks still load on apollo) must round-trip honestly rather than
 * be quietly rewritten by a decompiler. Forge can no longer PRODUCE another type; it can still READ one.
 */
export const AAOB_VALUE_TYPE = 'String';

/** One Forge entity-reference intent: set `field` on the entity, linking `entityVar`'s entity. */
type ForgeEntityRef = { field: string; entityVar: string; considerEntity?: boolean };

/**
 * Minimal page-schema shape the guard needs. Declared structurally (not imported) so it is satisfied by
 * BOTH `PageContext` types in play — `block-json.types` (used by forge-compiler) and `forge.types` (used
 * by steps-to-blockly) — without coupling the guard to either.
 */
interface GuardSchema {
  localVariables?: { encodedId: string; holdsEntity?: string }[];
  globalVariables?: { encodedId: string; holdsEntity?: string }[];
  entities?: any[];
}

/**
 * A3 / #13 — Forge guard for `entity_reference_selection`.
 *
 * Drops any reference whose LINKED entity type does not match the reference FIELD's expected type. The
 * field encodes its target entity as `<path>%<referenceEntityName>_for_CRUD`; the linked entity's type is
 * the `holdsEntity` of its variable. A mismatch means Forge chose the wrong entity — writing that UUID
 * would land in a foreign reference column (points at the wrong table's row).
 *
 * Doing this at GENERATION (with the full page schema in hand) is clean and reliable — unlike a runtime
 * check, which only has the resolved value and must fuzzily re-derive the type. Editor-authored blocks are
 * covered separately by the on-attach guard; this closes the Forge / hand-authored path.
 *
 * FAIL-OPEN: keeps any ref whose expected OR linked type cannot be resolved (never drops a valid ref on
 * uncertainty). Every dropped ref is pushed to `warnings` so it surfaces in the Forge result.
 */
export function guardEntityReferences<T extends ForgeEntityRef>(
  refs: T[],
  pageContext: GuardSchema,
  warnings: string[]
): T[] {
  if (!Array.isArray(refs)) return refs;
  return refs.filter((ref) => {
    if (!ref || ref.considerEntity === false) return true; // not linking an entity → nothing to check
    const expected = parseExpectedType(ref.field);
    const linked = resolveLinkedEntity(ref.entityVar, pageContext);
    if (!expected || !linked) return true; // fail-open: can't judge → keep
    if (entitiesMatch(expected, linked, pageContext)) return true;
    warnings.push(
      `entity_reference_selection: dropped reference "${ref.field}" — linked entity "${linked}" is not ` +
        `the type this field references ("${expected}"). A wrong-entity reference would point at the wrong table.`
    );
    return false;
  });
}

/** Reference field encodes its target entity as `…%<referenceEntityName>_for_CRUD` (last segment). */
function parseExpectedType(field: string): string | null {
  const m = /%([^%]+?)_for_CRUD$/.exec(field || '');
  return m ? m[1] : null;
}

/** The entity a linked variable holds = its `holdsEntity` (`entityName || entityId`). */
function resolveLinkedEntity(entityVar: string, pc: GuardSchema): string | null {
  if (!entityVar || typeof entityVar !== 'string') return null;
  const all = [...(pc.localVariables || []), ...(pc.globalVariables || [])];
  const v = all.find((x) => x.encodedId === entityVar);
  return v?.holdsEntity || null;
}

/** Match id-vs-name safely by canonicalising both identifiers through the entity list. */
function entitiesMatch(a: string, b: string, pc: GuardSchema): boolean {
  if (a === b) return true;
  const canon = (x: string): string => {
    const e = (pc.entities || []).find((en: any) => en.id === x || en.name === x);
    return e ? e.id : x;
  };
  return canon(a) === canon(b);
}

/** The blocks always allowed to target an AAOB (extendedAttributes) reference. The first 5 are the
 *  dedicated AAOB blocks (write via the upsert, read via aggregate/filter/label/sort). Beyond these, AAOB is
 *  READ-ONLY: any `get_*` reader may target it, but general WRITE blocks (set_*, push) may not — EXCEPT
 *  `set_reference_entity_variable_for_AOB`, which is allowed because it sets a REFERENCE entity for the AOB
 *  (plain OR AAOB), not the systemKey/value columns, so it does not bypass the upsert (SET_AOB_REF /
 *  SET_AOB_FROM_AOB compile to it). See docs/blockly/aaob-model.md. */
const AAOB_ALLOWED_BLOCKS = new Set([
  'set_key_value_in_AOB_reference',
  'aaob_aggregate_by_label',
  'aaob_filter_by_label',
  'aaob_label_exists',
  'aaob_sort_by_label',
  'set_reference_entity_variable_for_AOB',
]);

/**
 * True when a block that MUST NOT target an AAOB does so — i.e. a general WRITE AOB block (set_*, push)
 * whose VAR points at an AAOB reference. AAOB is READ-ONLY outside the dedicated blocks: readers (`get_*`)
 * and the dedicated AAOB blocks are allowed; everything else writing an AAOB is a violation.
 * `encodedId` belongs to an AAOB ref iff it equals — or starts with (`<refId>$`) — the id of an
 * `entityListReferences` entry whose `extendedAttributes` is true. FAIL-CLOSED only on a real match.
 */
export function isGeneralAobBlockTargetingAaob(
  blockType: string,
  encodedId: string,
  entityListReferences: { id: string; extendedAttributes?: boolean }[] | undefined
): boolean {
  // Allowed to target AAOB: the dedicated AAOB blocks + any READER (get_* is read-only).
  if (!blockType || !encodedId || AAOB_ALLOWED_BLOCKS.has(blockType) || blockType.startsWith('get_')) return false;
  for (const e of entityListReferences || []) {
    if (!e?.extendedAttributes || !e.id) continue;
    if (encodedId === e.id || encodedId.startsWith(e.id + '$')) return true;
  }
  return false;
}
